import React from 'react'
import { CheckCircle, Copy } from 'lucide-react'

export default function CreatePartnerModal({ 
  showCreateModal, 
  setShowCreateModal, 
  newCredentials, 
  setNewCredentials,
  copiedField,
  copyToClipboard,
  loading,
  handleCreatePartner
}) {
  if (!showCreateModal) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-xl max-w-md w-full p-6">
        {newCredentials ? (
          <CredentialsDisplay 
            credentials={newCredentials}
            copiedField={copiedField}
            copyToClipboard={copyToClipboard}
            onClose={() => {
              setShowCreateModal(false)
              setNewCredentials(null)
            }}
          />
        ) : (
          <CreatePartnerForm 
            onSubmit={handleCreatePartner}
            onCancel={() => setShowCreateModal(false)}
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

function CredentialsDisplay({ credentials, copiedField, copyToClipboard, onClose }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-green-500/20 rounded-full">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Partner Created!</h2>
      </div>
      
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
        <p className="text-yellow-800 text-sm font-medium">
          ⚠️ Save these credentials now - the client secret will not be shown again!
        </p>
      </div>

      <div className="space-y-4">
        <CredentialField 
          label="Partner ID"
          value={credentials.partner_id}
          fieldKey="partner_id"
          copiedField={copiedField}
          copyToClipboard={copyToClipboard}
        />
        <CredentialField 
          label="Client ID"
          value={credentials.client_id}
          fieldKey="client_id"
          copiedField={copiedField}
          copyToClipboard={copyToClipboard}
          small
        />
        <CredentialField 
          label="Client Secret"
          value={credentials.client_secret}
          fieldKey="client_secret"
          copiedField={copiedField}
          copyToClipboard={copyToClipboard}
          highlight
        />
      </div>

      <button
        onClick={onClose}
        className="w-full mt-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
      >
        Done
      </button>
    </div>
  )
}

function CredentialField({ label, value, fieldKey, copiedField, copyToClipboard, small, highlight }) {
  return (
    <div>
      <label className="text-sm font-medium text-zinc-200">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <code className={`flex-1 px-3 py-2 rounded font-mono ${
          highlight 
            ? 'bg-red-500/10 border border-red-500/30 text-sm' 
            : 'bg-zinc-800'
        } ${small ? 'text-xs' : 'text-sm'}`}>
          {value}
        </code>
        <button
          onClick={() => copyToClipboard(value, fieldKey)}
          className="p-2 hover:bg-zinc-800 rounded"
        >
          {copiedField === fieldKey ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  )
}

function CreatePartnerForm({ onSubmit, onCancel, loading }) {
  return (
    <form onSubmit={onSubmit}>
      <h2 className="text-xl font-semibold mb-4">New Integration Partner</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-zinc-200 mb-1">
            Partner Name *
          </label>
          <input
            name="name"
            type="text"
            required
            className="w-full px-3 py-2 border border-zinc-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-indigo-500"
            placeholder="e.g., ABC Registration System"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-200 mb-1">
            Contact Email *
          </label>
          <input
            name="contact_email"
            type="email"
            required
            className="w-full px-3 py-2 border border-zinc-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-indigo-500"
            placeholder="admin@partner.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-200 mb-1">
            Description
          </label>
          <textarea
            name="description"
            rows={3}
            className="w-full px-3 py-2 border border-zinc-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-indigo-500"
            placeholder="Brief description of the integration..."
          />
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-zinc-600 text-zinc-200 rounded-lg hover:bg-zinc-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Partner'}
        </button>
      </div>
    </form>
  )
}
